import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import {
  usePromptOverride,
  useResetPromptOverride,
  useSavePromptOverride,
  type PromptOverride,
} from './usePromptOverride';

export function PromptPage({ slug }: { slug: string }) {
  const { data, isLoading, error } = usePromptOverride(slug);
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/prompts"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Prompts
        </Link>
        <h2 className="text-3xl font-semibold tracking-tight">
          {data?.title ?? 'Prompt'}
        </h2>
        {data ? (
          <p className="text-sm text-muted-foreground">
            {data.description} A saved prompt applies to every user from its
            next use. Reset goes back to the prompt in code.
          </p>
        ) : null}
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive">{error.message}</p>
      ) : data ? (
        // Remounts with the saved prompt after a save or a reset
        <PromptEditor key={data.prompt} promptOverride={data} />
      ) : null}
    </div>
  );
}

function PromptEditor({ promptOverride }: { promptOverride: PromptOverride }) {
  const [draft, setDraft] = useState(promptOverride.prompt);
  const save = useSavePromptOverride(promptOverride.slug);
  const reset = useResetPromptOverride(promptOverride.slug);
  const toast = useToast();

  const isPending = save.isPending || reset.isPending;
  const canSave =
    !isPending &&
    draft.trim().length > 0 &&
    draft.trim() !== promptOverride.prompt.trim();

  const handleSave = async () => {
    if (!window.confirm('Use this prompt for every user from its next use?'))
      return;
    try {
      await save.mutateAsync(draft);
      toast.success('Prompt saved');
    } catch (e) {
      toast.error(
        'Could not save the prompt',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Go back to the prompt in code?')) return;
    try {
      await reset.mutateAsync();
      toast.success('Prompt reset to the default');
    } catch (e) {
      toast.error(
        'Could not reset the prompt',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm">
        {promptOverride.is_overridden
          ? 'Using an edited prompt.'
          : 'Using the default prompt from code.'}
      </p>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={30}
        className="font-mono text-xs"
        aria-label="Prompt"
      />
      <div className="flex gap-2">
        <Button disabled={!canSave} onClick={() => void handleSave()}>
          Save
        </Button>
        <Button
          variant="outline"
          disabled={isPending || !promptOverride.is_overridden}
          onClick={() => void handleReset()}
        >
          Reset to default
        </Button>
      </div>
    </div>
  );
}
